import {
	Label,
	Listbox,
	ListboxButton,
	ListboxOption,
	ListboxOptions,
	Transition,
} from '@headlessui/react'
import { ArrowDownTrayIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid'
import { NavLink, Outlet, useLocation, useNavigate } from '@remix-run/react'
import { Fragment, useState } from 'react'

const tabs = [
	{ name: 'Monday', href: 'monday', fl: 'M' },
	{ name: 'Tuesday', href: 'tuesday', fl: 'T' },
	{ name: 'Wednesday', href: 'wednesday', fl: 'W' },
	{ name: 'Thursday', href: 'thursday', fl: 'T' },
	{ name: 'Friday', href: 'friday', fl: 'F' },
	{ name: 'Saturday', href: 'saturday', fl: 'S' },
]

const sortByClass = [
	{
		id: 1,
		name: 'All',
		classSort: 'all',
	},
	{
		id: 2,
		name: 'Wee Dance',
		classSort: 'wee',
	},
	{
		id: 3,
		name: 'Twinkling Stars',
		classSort: 'stars',
	},
	{
		id: 4,
		name: 'Ballet',
		classSort: 'ballet',
	},
	{
		id: 5,
		name: 'Hip Hop',
		classSort: 'hiphop',
	},
	{
		id: 6,
		name: 'Jazz',
		classSort: 'jazz',
	},
	{
		id: 7,
		name: 'Contemporary/Lyrical',
		classSort: 'cont-lyr',
	},
	{
		id: 8,
		name: 'Lyrical',
		classSort: 'lyrical',
	},
	{
		id: 9,
		name: 'Pointe',
		classSort: 'pointe',
	},
	{
		id: 10,
		name: 'Tap',
		classSort: 'tap',
	},
	{
		id: 11,
		name: 'Musical Theatre',
		classSort: 'mt',
	},
	{
		id: 12,
		name: 'Conditioning/Technique',
		classSort: 'technique',
	},
	{
		id: 13,
		name: 'Industry',
		classSort: 'industry',
	},
	{
		id: 14,
		name: 'Mat Pilates',
		classSort: 'mat',
	},
]

const sortByAge = [
	{ id: 1, name: 'All', ageSort: 'all' },
	{ id: 2, name: 'Ages 18 - 36 months', ageSort: 'level1' },
	{ id: 3, name: 'Ages 3 - 5', ageSort: 'level2' },
	{ id: 4, name: 'Ages 5 - 7', ageSort: 'level3' },
	{ id: 5, name: 'Ages 7 - 9', ageSort: 'level4' },
	{ id: 6, name: 'Ages 10 & Up', ageSort: 'level5' },
	{ id: 7, name: 'Adult', ageSort: 'level6' },
]

function classNames(...classes: string[]) {
	return classes.filter(Boolean).join(' ')
}

export default function Classes() {
	const [classSelected, setClassSelected] = useState(sortByClass[0])
	const [ageSelected, setAgeSelected] = useState(sortByAge[0])
	const location = useLocation()
	const navigate = useNavigate()

	function applyFilters(event: React.FormEvent<HTMLFormElement>) {
		event.preventDefault()
		const params = new URLSearchParams()
		params.set('class', classSelected?.classSort ?? 'all')
		params.set('age', ageSelected?.ageSort ?? 'all')
		const next = `${location.pathname}?${params.toString()}`
		const current = `${location.pathname}${location.search}`
		if (next === current) return
		navigate(next, { preventScrollReset: true })
	}

	return (
		<div className="relative isolate min-h-screen overflow-hidden bg-stone-900 pb-24">
			<div className="mx-auto mt-24 max-w-screen-2xl px-4 py-10 sm:px-6 sm:py-16 lg:px-8">
				<div className="absolute inset-x-0 top-1/2 -z-10 -translate-y-1/2 transform-gpu overflow-hidden opacity-30 blur-3xl">
					<svg
						viewBox="0 0 1313 771"
						aria-hidden="true"
						className="ml-[max(50%,38rem)] w-[82.0625rem]"
					>
						<path
							id="bc169a03-3518-42d4-ab1e-d3eadac65edc"
							fill="url(#85a0eca5-25f1-4ab9-af84-4e2d8d9cdbf3)"
							d="M360.508 589.796 231.671 770.522 0 498.159l360.508 91.637 232.034-325.485c1.485 150.396 51.235 393.965 238.354 165.069C1064.79 143.261 1002.42-107.094 1171.72 46.97c135.44 123.252 148.51 335.641 138.11 426.428L971.677 339.803l24.062 411.461-635.231-161.468Z"
						/>
						<defs>
							<linearGradient
								id="85a0eca5-25f1-4ab9-af84-4e2d8d9cdbf3"
								x1="1313.17"
								x2="-88.881"
								y1=".201"
								y2="539.417"
								gradientUnits="userSpaceOnUse"
							>
								<stop stopColor="#9089FC" />
								<stop offset={1} stopColor="#FF80B5" />
							</linearGradient>
						</defs>
					</svg>
				</div>
				<div className="absolute inset-x-0 top-0 -z-10 flex transform-gpu overflow-hidden pt-32 opacity-25 blur-3xl sm:pt-40 xl:justify-end">
					<svg
						viewBox="0 0 1313 771"
						aria-hidden="true"
						className="ml-[-22rem] w-[82.0625rem] flex-none origin-top-right rotate-[30deg] xl:ml-0 xl:mr-[calc(50%-12rem)]"
					>
						<use href="#bc169a03-3518-42d4-ab1e-d3eadac65edc" />
					</svg>
				</div>
				<div className="flex items-center">
					<div className="flex-auto">
						<h2 className="text-base font-semibold uppercase tracking-wide text-stone-200">
							2026-27
						</h2>
						<h3 className="mt-2 text-3xl font-extrabold leading-8 text-stone-50 sm:text-4xl">
							Class Schedule
						</h3>
						<p className="mt-2 text-sm text-stone-300">
							Classes run from September 8, 2026 to June 6, 2027.
						</p>
					</div>
					<div className="ml-4 mt-4 sm:ml-16 sm:mt-0">
						<a
							href="https://docs.google.com/spreadsheets/d/1cc4q2TElvy9OxHWvPsANKIJW81LAM-XefdL3dn56jD4/edit?gid=1895210090#gid=1895210090"
							target="_blank"
							rel="noreferrer"
						>
							<button
								type="button"
								className="inline-flex items-center justify-center rounded-full border border-transparent bg-stone-50 p-2 text-sm font-medium text-stone-900 shadow-sm hover:bg-stone-300 focus:outline-none sm:w-auto"
							>
								<ArrowDownTrayIcon className="h-4 w-4 sm:h-6 sm:w-6" />
							</button>
						</a>
					</div>
				</div>
				<div className="mx-auto max-w-2xl pt-8">
					<form
						onSubmit={applyFilters}
						className="w-full items-end justify-between gap-x-3 space-y-6 sm:flex sm:gap-x-6 sm:space-y-0"
					>
						<div className="w-full">
							<Listbox value={classSelected} onChange={setClassSelected}>
								{({ open }) => (
									<>
										<Label className="block text-sm font-medium leading-6 text-white">
											Filter By Class
										</Label>
										<div className="relative mt-2">
											<ListboxButton
												type="button"
												className="relative w-full cursor-default rounded-md bg-white py-1.5 pl-3 pr-10 text-left text-sm text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-600 sm:leading-6"
											>
												<span className="block truncate">
													{classSelected?.name}
												</span>
												<span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
													<ChevronUpDownIcon
														className="h-5 w-5 text-gray-400"
														aria-hidden="true"
													/>
												</span>
											</ListboxButton>

											<Transition
												show={open}
												as={Fragment}
												leave="transition ease-in duration-100"
												leaveFrom="opacity-100"
												leaveTo="opacity-0"
											>
												<ListboxOptions className="absolute z-40 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-xs shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
													{sortByClass.map((c) => (
														<ListboxOption
															key={c.id}
															className="relative cursor-default select-none py-2 pl-8 pr-4 text-sm data-[focus]:bg-purple-600 data-[focus]:text-white"
															value={c}
														>
															{({ selected }) => (
																<>
																	<span
																		className={classNames(
																			selected
																				? 'font-semibold'
																				: 'font-normal',
																			'block truncate',
																		)}
																	>
																		{c.name}
																	</span>
																</>
															)}
														</ListboxOption>
													))}
												</ListboxOptions>
											</Transition>
										</div>
									</>
								)}
							</Listbox>
						</div>
						<div className="w-full">
							<Listbox value={ageSelected} onChange={setAgeSelected}>
								{({ open }) => (
									<>
										<Label className="block text-sm font-medium leading-6 text-white">
											Filter By Age
										</Label>
										<div className="relative mt-2">
											<ListboxButton
												type="button"
												className="relative w-full cursor-default rounded-md bg-white py-1.5 pl-3 pr-10 text-left text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-600 sm:text-sm sm:leading-6"
											>
												<span className="block truncate">
													{ageSelected?.name}
												</span>
												<span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
													<ChevronUpDownIcon
														className="h-5 w-5 text-gray-400"
														aria-hidden="true"
													/>
												</span>
											</ListboxButton>

											<Transition
												show={open}
												as={Fragment}
												leave="transition ease-in duration-100"
												leaveFrom="opacity-100"
												leaveTo="opacity-0"
											>
												<ListboxOptions className="absolute z-40 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
													{sortByAge.map((a) => (
														<ListboxOption
															key={a.id}
															className="relative cursor-default select-none py-2 pl-8 pr-4 text-sm data-[focus]:bg-purple-600 data-[focus]:text-white"
															value={a}
														>
															{({ selected }) => (
																<>
																	<span
																		className={classNames(
																			selected
																				? 'font-semibold'
																				: 'font-normal',
																			'block truncate',
																		)}
																	>
																		{a.name}
																	</span>
																</>
															)}
														</ListboxOption>
													))}
												</ListboxOptions>
											</Transition>
										</div>
									</>
								)}
							</Listbox>
						</div>
						<div>
							<button
								type="submit"
								className="font-medim rounded bg-[#f9c04d] px-4 py-2 text-stone-900"
							>
								Filter
							</button>
						</div>
					</form>
				</div>
				<div className="sticky top-0 z-20 mt-16 py-4 sm:hidden">
					<nav
						className="flex items-center justify-center space-x-4 font-bold uppercase"
						aria-label="Tabs"
					>
						{tabs.map((tab) => (
							<NavLink
								preventScrollReset={true}
								key={tab.name}
								to={{ pathname: tab.href, search: location.search }}
								className={({ isActive }) =>
									isActive
										? 'rounded-md bg-stone-100 px-3 py-2 text-sm font-medium text-stone-700'
										: 'rounded-md px-3 py-2 text-sm font-bold text-[#f9c04d] hover:text-stone-50'
								}
							>
								{tab.fl}
							</NavLink>
						))}
					</nav>
				</div>

				<div className="sticky top-0 z-20 mt-16 hidden py-4 sm:block">
					<nav
						className="flex items-center justify-center space-x-4 font-bold uppercase"
						aria-label="Tabs"
					>
						{tabs.map((tab) => (
							<NavLink
								preventScrollReset={true}
								key={tab.name}
								to={{ pathname: tab.href, search: location.search }}
								className={({ isActive }) =>
									isActive
										? 'rounded-md bg-stone-100 px-3 py-2 text-xs font-medium text-stone-700 md:text-sm'
										: 'rounded-md px-3 py-2 text-xs font-bold tracking-wider text-[#f9c04d] hover:text-stone-50 md:text-sm'
								}
							>
								{tab.name}
							</NavLink>
						))}
					</nav>
				</div>
				<Outlet />
				<div className="mt-16 sm:mt-20 lg:mt-24">
					<p className="text-sm leading-5 text-stone-200">
						* Means accelerated level. Entry into class by teacher approval
						only.
					</p>
				</div>
			</div>
		</div>
	)
}
